import { formatCurrency } from "./helpers";

export interface CommercialOfferPrintItem {
  art?: string | null;
  name?: string | null;
  details?: string | null;
  imageUrl?: string | null;
  quantity: number;
  unitPrice: number;
}

interface OpenCommercialOfferPrintPageParams {
  items: CommercialOfferPrintItem[];
  totalPrice: number;
  totalQuantity: number;
  customerName?: string | null;
  orderName?: string | null;
}

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDate = () =>
  new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date());

export const openCommercialOfferPrintPage = ({
  items,
  totalPrice,
  totalQuantity,
  customerName,
  orderName,
}: OpenCommercialOfferPrintPageParams) => {
  if (typeof window === "undefined") return false;

  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;

  printWindow.opener = null;

  const rows = items
    .map((item, index) => {
      const lineTotal = item.unitPrice * item.quantity;
      const details = item.details ? `<div class="details">${escapeHtml(item.details)}</div>` : "";
      const image = item.imageUrl
        ? `<img class="product-image" src="${escapeHtml(item.imageUrl)}" alt="" onerror="this.style.display='none'" />`
        : "";

      return `
        <tr>
          <td>${index + 1}</td>
          <td>
            <div class="product">
              ${image}
              <div>
                <div class="name">${escapeHtml(item.name) || "Товар"}</div>
                ${item.art ? `<div class="art">Арт. ${escapeHtml(item.art)}</div>` : ""}
                ${details}
              </div>
            </div>
          </td>
          <td class="number">${item.quantity}</td>
          <td class="number">${formatCurrency(item.unitPrice)} ₽</td>
          <td class="number total">${formatCurrency(lineTotal)} ₽</td>
        </tr>`;
    })
    .join("");

  const recipient = [customerName, orderName].filter(Boolean).map(escapeHtml).join(" · ");

  printWindow.document.open();
  printWindow.document.write(`<!doctype html>
<html lang="ru">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Коммерческое предложение</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; background: #f4f3f0; color: #151515; font-family: Arial, sans-serif; }
      .toolbar { position: sticky; top: 0; z-index: 1; padding: 16px; background: rgba(244, 243, 240, .92); text-align: center; }
      button { border: 0; border-radius: 10px; padding: 12px 18px; background: #151515; color: #fff; font-size: 14px; font-weight: 700; cursor: pointer; }
      .document { width: min(210mm, calc(100% - 32px)); min-height: 297mm; margin: 0 auto 32px; padding: 22mm 18mm; background: #fff; }
      .brand { font-size: 28px; font-weight: 800; letter-spacing: 1.2px; }
      .eyebrow { margin-top: 38px; color: #777; font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
      h1 { margin: 7px 0 0; font-size: 30px; line-height: 1.1; }
      .meta { display: flex; justify-content: space-between; gap: 16px; margin: 28px 0; padding: 15px 0; border-top: 1px solid #ddd; border-bottom: 1px solid #ddd; color: #555; font-size: 13px; }
      .meta strong { color: #151515; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th { padding: 10px 8px; background: #151515; color: #fff; font-size: 10px; letter-spacing: .5px; text-align: left; text-transform: uppercase; }
      td { padding: 13px 8px; border-bottom: 1px solid #e4e4e4; vertical-align: top; }
      .number { text-align: right; white-space: nowrap; }
      .name { font-weight: 700; }
      .product { display: flex; align-items: flex-start; gap: 10px; min-width: 180px; }
      .product-image { width: 54px; height: 54px; flex: 0 0 54px; border-radius: 7px; object-fit: contain; background: #f6f5f2; }
      .art, .details { margin-top: 3px; color: #777; font-size: 11px; }
      .total { font-weight: 700; }
      .summary { display: flex; justify-content: flex-end; margin-top: 22px; }
      .summary-box { min-width: 240px; padding-top: 12px; border-top: 2px solid #151515; }
      .summary-row { display: flex; justify-content: space-between; gap: 24px; margin-top: 6px; font-size: 14px; }
      .summary-row strong { font-size: 20px; }
      .footer { margin-top: 58px; color: #777; font-size: 11px; line-height: 1.5; }
      @page { size: A4; margin: 0; }
      @media print {
        body { background: #fff; }
        .toolbar { display: none; }
        .document { width: 100%; min-height: 0; margin: 0; padding: 18mm; }
      }
    </style>
  </head>
  <body>
    <div class="toolbar"><button type="button" onclick="window.print()">Печать / сохранить PDF</button></div>
    <main class="document">
      <div class="brand">DEANTE</div>
      <div class="eyebrow">Коммерческое предложение</div>
      <h1>Подборка товаров</h1>
      <div class="meta">
        <div>Дата формирования: <strong>${formatDate()}</strong></div>
        ${recipient ? `<div>Получатель: <strong>${recipient}</strong></div>` : ""}
      </div>
      <table>
        <thead><tr><th>№</th><th>Товар</th><th class="number">Кол-во</th><th class="number">Цена</th><th class="number">Сумма</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="summary">
        <div class="summary-box">
          <div class="summary-row"><span>Товаров</span><span>${items.length} поз.</span></div>
          <div class="summary-row"><span>Количество</span><span>${totalQuantity} шт.</span></div>
          <div class="summary-row"><strong>Итого</strong><strong>${formatCurrency(totalPrice)} ₽</strong></div>
        </div>
      </div>
      <div class="footer">Предложение сформировано на основании актуальных данных корзины. Итоговые условия и наличие уточняются менеджером.</div>
    </main>
  </body>
</html>`);
  printWindow.document.close();
  printWindow.focus();

  return true;
};
